/**
 * Builds a selectable hierarchy tree from flat enterprise lists.
 * Port of admin `buildHierarchy` (orthographiq-b2b-admin-gitlab).
 *
 * @param {object|null} enterprise
 * @param {object[]} subsidiaries
 * @param {object[]} departments
 * @param {object[]} teams
 * @returns {object[]} root nodes
 *
 * @created_at 29/09/2026 by AI (Composer)
 */
export function buildHierarchyTree(enterprise, subsidiaries, departments, teams) {
	if (enterprise == null) {
		return [];
	}

	const enterpriseId = toIdString(enterprise._id);
	const enterpriseNode = {
		id: enterpriseId,
		name: enterprise.name,
		level: 0,
		expanded: true,
		type: 'enterprise',
		children: [],
	};

	const subsidiaryNodesMap = {};
	const departmentNodesMap = {};

	const subsidiaryList = subsidiaries || [];
	for (let i = 0; i < subsidiaryList.length; i++) {
		const sub = subsidiaryList[i];
		if (toIdString(sub.entreprise_id) !== enterpriseId) {
			continue;
		}
		const subsidiaryNode = {
			id: toIdString(sub._id),
			name: sub.name,
			level: 1,
			expanded: true,
			type: 'subsidiary',
			children: [],
		};
		enterpriseNode.children.push(subsidiaryNode);
		subsidiaryNodesMap[subsidiaryNode.id] = subsidiaryNode;
	}

	const departmentList = departments || [];
	for (let j = 0; j < departmentList.length; j++) {
		const dept = departmentList[j];
		if (toIdString(dept.entreprise_id) !== enterpriseId) {
			continue;
		}
		let parentNode = null;
		if (dept.subsidiary_id) {
			parentNode = subsidiaryNodesMap[toIdString(dept.subsidiary_id)] || null;
		}
		if (!parentNode) {
			parentNode = enterpriseNode;
		}
		const departmentNode = {
			id: toIdString(dept._id),
			name: dept.name,
			level: parentNode === enterpriseNode ? 1 : 2,
			expanded: true,
			type: 'department',
			children: [],
		};
		parentNode.children.push(departmentNode);
		departmentNodesMap[departmentNode.id] = departmentNode;
	}

	const teamList = teams || [];
	for (let k = 0; k < teamList.length; k++) {
		const team = teamList[k];
		if (toIdString(team.entreprise_id) !== enterpriseId) {
			continue;
		}
		let parentNode = null;
		if (team.department_id) {
			parentNode = departmentNodesMap[toIdString(team.department_id)] || null;
		}
		if (!parentNode && team.subsidiary_id) {
			parentNode = subsidiaryNodesMap[toIdString(team.subsidiary_id)] || null;
		}
		if (!parentNode) {
			parentNode = enterpriseNode;
		}
		const teamNode = {
			id: toIdString(team._id),
			name: team.name,
			level: parentNode === enterpriseNode ? 1 : 3,
			type: 'team',
			children: [],
		};
		parentNode.children.push(teamNode);
	}

	return [enterpriseNode];
}

/**
 * @param {*} id
 * @returns {string}
 *
 * @created_at 29/09/2026 by AI (Composer)
 */
function toIdString(id) {
	if (id == null) {
		return '';
	}
	return String(id);
}
